import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const Wrapper = styled.div`
  position: relative;
  padding: 1rem;
  background: #f9f9f9;
  height: 70vh;
`;

const FiltersWrapper = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  justify-content: center;
`;

const InfoIconWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 15px;
  cursor: pointer;
  color: #666;
  &:hover { color: #000; }
  svg { width: 20px; height: 20px; }
`;

/** === helpers === */

// unified sales calc
const calcSale = (row) =>
  Math.max(
    0,
    parseFloat(
      row.K_15 || row.K_19 || row.K_10 || row.K_21 || row.K_17 || row.K_11 || row.K_22
    ) || 0
  );

// ISO week number (1..53), Monday first
function getISOWeek(dateStr) {
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7; // 1..7 (Mon..Sun)
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((date - yearStart) / 86400000 + 1) / 7);
  return weekNo;
}

// ISO year of the given date
function getISOYear(dateStr) {
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  return date.getUTCFullYear();
}

// simple moving average (N)
function movingAverage(arr, window = 4) {
  const out = new Array(arr.length).fill(null);
  let sum = 0;
  for (let i = 0; i < arr.length; i++) {
    sum += arr[i] || 0;
    if (i >= window) sum -= arr[i - window] || 0;
    if (i >= window - 1) out[i] = +(sum / window).toFixed(2);
  }
  return out;
}

const WeeklySalesComparisonWithTrends = ({ data }) => {
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);

  useEffect(() => {
    if (!data || data.length === 0) return;
    const years = new Set();
    for (const row of data) {
      if (!row.DataWystawienia) continue;
      const isoYear = getISOYear(row.DataWystawienia);
      if (isoYear) years.add(isoYear);
    }
    const sortedYears = Array.from(years).sort((a, b) => a - b);
    setAvailableYears(sortedYears);
    if (!selectedYear && sortedYears.length) {
      setSelectedYear(sortedYears[sortedYears.length - 1]);
    }
  }, [data]);

  const chartData = useMemo(() => {
    if (!selectedYear || !data) return null;

    const weeksCount = 53; // 1..53
    const curr = new Array(weeksCount).fill(0);
    const prev = new Array(weeksCount).fill(0);

    data.forEach((row) => {
      const ds = row.DataWystawienia;
      if (!ds) return;
      const isoYear = getISOYear(ds);
      const isoWeek = getISOWeek(ds);
      if (!isoYear || !isoWeek) return;
      const v = calcSale(row);
      if (v <= 0) return;

      if (isoYear === selectedYear) {
        curr[isoWeek - 1] += v;
      } else if (isoYear === selectedYear - 1) {
        prev[isoWeek - 1] += v;
      }
    });

    const currTrend = movingAverage(curr, 4);
    const prevTrend = movingAverage(prev, 4);

    // palette
    const GREEN  = "#2e7d32";
    const ORANGE = "#fb8c00";

    const labels = Array.from({ length: weeksCount }, (_, i) => `${i + 1}`);

    return {
      labels,
      datasets: [
        {
          label: `Tygodniowa sprzedaż ${selectedYear}`,
          data: curr,
          type: "bar",
          backgroundColor: "rgba(46, 125, 50, 0.70)", // uniform green bars
          borderColor: GREEN,
          borderWidth: 1,
          order: 2,
        },
        {
          label: `Tygodniowa sprzedaż ${selectedYear - 1}`,
          data: prev,
          type: "bar",
          backgroundColor: "rgba(251,140,0,0.60)", // orange bars
          borderColor: ORANGE,
          borderWidth: 1,
          order: 2,
        },
        {
          label: `Trend ${selectedYear}`,
          data: currTrend,
          type: "line",
          borderColor: GREEN,              // current-year trend = green
          backgroundColor: "rgba(46,125,50,0.15)",
          fill: false,
          tension: 0.35,
          pointRadius: 0,
          borderWidth: 2,
          order: 1,
        },
        {
          label: `Trend ${selectedYear - 1}`,
          data: prevTrend,
          type: "line",
          borderColor: ORANGE,             // previous-year trend = orange
          backgroundColor: "rgba(251,140,0,0.15)",
          fill: false,
          tension: 0.35,
          pointRadius: 0,
          borderWidth: 2,
          order: 1,
        },
      ],
    };
  }, [selectedYear, data]);

  if (!chartData) return <p>Ładowanie danych...</p>;

  return (
    <Wrapper>
      <InfoIconWrapper data-tooltip-id="weekly-comp-trend-tip">
        <Info />
      </InfoIconWrapper>
      <InfoTooltip
        id="weekly-comp-trend-tip"
        place="left"
        content={
          <>
            <strong>Sprzedaż tygodniowa z trendami</strong><br />
            Słupki: tygodnie 1–53 ({selectedYear} zielone, {selectedYear - 1} pomarańczowe).<br />
            Linie: 4-tygodniowa średnia krocząca dla obu lat.
          </>
        }
      />

      <FiltersWrapper>
        <label>
          Rok:&nbsp;
          <select
            value={selectedYear || ""}
            onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
          >
            {availableYears.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </label>
      </FiltersWrapper>

      <Bar
        data={chartData}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: "top" },
            title: {
              display: true,
              text: `Sprzedaż tygodniowa – ${selectedYear} vs ${selectedYear - 1}`,
            },
            tooltip: {
              mode: "index",
              intersect: false,
              callbacks: {
                label: (ctx) => {
                  const val = ctx.parsed?.y ?? ctx.raw ?? 0;
                  return `${ctx.dataset.label}: ${Number(val).toLocaleString("pl-PL", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`;
                },
              },
            },
          },
          scales: {
            x: {
              title: { display: true, text: "Tydzień (ISO 1–53)" },
              stacked: false,
              ticks: {
                maxRotation: 0,
                autoSkip: true,
                maxTicksLimit: 26,
              },
            },
            y: {
              beginAtZero: true,
              title: { display: true, text: "Wartość sprzedaży" },
              ticks: {
                callback: (v) =>
                  Number(v).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " "),
              },
            },
          },
          interaction: { mode: "index", intersect: false },
        }}
      />
    </Wrapper>
  );
};

export default WeeklySalesComparisonWithTrends;
